import { useEffect } from 'react';

interface CourseSocialCardProps {
  course: {
    id: string;
    name: string;
    description?: string;
    what_awaits_you?: string;
    image_url?: string;
    individual_price?: number;
    group_price?: number;
    duration_months?: number;
    weekly_frequency?: number;
    format?: string[];
    learning_type?: string[];
    languages?: string[];
    instructors?: string[];
    admission_deadline?: string;
    start_date?: string;
    status: string;
    discount_percentage?: number;
    faculties?: {
      name: string;
      color: string;
      icon?: string;
    };
  };
  path: string;
}

export const CourseSocialCard = ({ course, path }: CourseSocialCardProps) => {
  useEffect(() => {
    const fullUrl = `https://globalitacademy.am${path}`;
    
    // Enhanced course description for social media
    const getSocialDescription = () => {
      const baseDesc = course.what_awaits_you || course.description || `Սովորեք ${course.name} դասընթացը Global IT Academy-ում`;
      const faculty = course.faculties?.name ? ` | ${course.faculties.name}` : '';
      const duration = course.duration_months ? ` | ${course.duration_months} ամիս` : '';
      const price = course.group_price ? ` | ${course.group_price.toLocaleString()} դր․/ամիս` : '';
      const discount = course.discount_percentage ? ` | ${course.discount_percentage}% զեղչ` : '';
      
      return `${baseDesc}${faculty}${duration}${price}${discount} | Երևան, Հայաստան`;
    };

    // Enhanced keywords for social media
    const getSocialKeywords = () => {
      const baseKeywords = [
        course.name,
        course.faculties?.name || 'ծրագրավորում',
        'Global IT Academy',
        'դասընթաց',
        'ուսուցում',
        'Երևան',
        'Հայաստան'
      ];

      // Add format-specific keywords
      if (course.format?.includes('online')) baseKeywords.push('օնլայն ուսուցում');
      if (course.format?.includes('offline') || course.format?.includes('inPerson')) baseKeywords.push('առկա ուսուցում');
      
      // Add learning type keywords
      if (course.learning_type?.includes('individual')) baseKeywords.push('անհատական ուսուցում');
      if (course.learning_type?.includes('group')) baseKeywords.push('խմբային ուսուցում');

      // Add status-specific keywords
      if (course.status === 'enrollment') baseKeywords.push('գրանցում բաց է');
      if (course.status === 'discount') baseKeywords.push('զեղչով');
      if (course.status === 'new') baseKeywords.push('նոր դասընթաց');

      return baseKeywords.join(', ');
    };

    // Get course image or fallback
    const getCourseImage = () => {
      // First priority: course's own image
      if (course.image_url) {
        // Ensure the URL is absolute
        if (course.image_url.startsWith('http')) {
          return course.image_url;
        }
        return `https://globalitacademy.am${course.image_url}`;
      }
      
      // Second priority: Faculty-specific fallback images from public/lovable-uploads
      const courseName = course.name?.toLowerCase() || '';
      const facultyName = course.faculties?.name?.toLowerCase() || '';
      
      // Backend courses - use programming image
      if (courseName.includes('backend') || courseName.includes('back-end') || 
          courseName.includes('back end') || facultyName.includes('backend')) {
        return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
      }
      
      // Frontend/Web courses - use web design image  
      if (courseName.includes('frontend') || courseName.includes('front-end') || 
          courseName.includes('front end') || courseName.includes('վեբ') || 
          courseName.includes('web') || facultyName.includes('frontend') || 
          facultyName.includes('վեբ') || facultyName.includes('web')) {
        return 'https://globalitacademy.am/lovable-uploads/3aef1e6b-6e35-4249-905f-4d58c0135648.png';
      }
      
      // Cybersecurity courses - use cybersecurity image
      if (courseName.includes('կիբեռ') || courseName.includes('cyber') || 
          courseName.includes('անվտանգություն') || courseName.includes('security') ||
          facultyName.includes('կիբեռ') || facultyName.includes('cyber')) {
        return 'https://globalitacademy.am/lovable-uploads/1d875431-108a-485d-a4e3-e27286786183.png';
      }
      
      // Mobile/Web Programming courses
      if (courseName.includes('վեբ ծրագրավորում') || courseName.includes('web programming') ||
          courseName.includes('mobile') || courseName.includes('մոբայլ')) {
        return 'https://globalitacademy.am/lovable-uploads/a3874ffc-061b-4601-a181-737a30e41c4a.png';
      }
      
      // Default programming image for any other course
      return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
    };

    const socialDescription = getSocialDescription();
    const socialKeywords = getSocialKeywords();
    const shareImage = getCourseImage();
    const courseTitle = `${course.name} | Global IT Academy`;

    console.log('📱 CourseSocialCard: Setting meta tags for course:', {
      name: course.name,
      image_url: course.image_url,
      faculty: course.faculties?.name,
      shareImage,
      courseTitle
    });

    // Helper function to create or update meta tag
    const createOrUpdateMeta = (selector: string, content: string, isProperty = false) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const attrName = isProperty ? 'property' : 'name';
        const attrValue = selector.replace(new RegExp(`meta\\[${attrName}="([^"]+)"\\]`), '$1');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update document title
    document.title = courseTitle;
    
    // Basic meta tags
    createOrUpdateMeta('meta[name="description"]', socialDescription);
    createOrUpdateMeta('meta[name="keywords"]', socialKeywords);

    // Open Graph tags for Facebook, LinkedIn, etc.
    createOrUpdateMeta('meta[property="og:title"]', courseTitle, true);
    createOrUpdateMeta('meta[property="og:description"]', socialDescription, true);
    createOrUpdateMeta('meta[property="og:url"]', fullUrl, true);
    createOrUpdateMeta('meta[property="og:image"]', shareImage, true);
    createOrUpdateMeta('meta[property="og:image:secure_url"]', shareImage, true);
    createOrUpdateMeta('meta[property="og:image:type"]', 'image/png', true);
    createOrUpdateMeta('meta[property="og:image:width"]', '1200', true);
    createOrUpdateMeta('meta[property="og:image:height"]', '630', true);
    createOrUpdateMeta('meta[property="og:image:alt"]', courseTitle, true);
    createOrUpdateMeta('meta[property="og:type"]', 'product', true);
    createOrUpdateMeta('meta[property="og:site_name"]', 'Global IT Academy', true);
    createOrUpdateMeta('meta[property="og:locale"]', 'hy_AM', true);

    // Course-specific Open Graph tags
    createOrUpdateMeta('meta[property="product:brand"]', 'Global IT Academy', true);
    createOrUpdateMeta('meta[property="product:category"]', course.faculties?.name || 'IT Education', true);
    
    if (course.group_price) {
      createOrUpdateMeta('meta[property="product:price:amount"]', course.group_price.toString(), true);
      createOrUpdateMeta('meta[property="product:price:currency"]', 'AMD', true);
    }
    
    createOrUpdateMeta('meta[property="product:availability"]', 'in stock', true);
    createOrUpdateMeta('meta[property="product:condition"]', 'new', true);

    // Twitter Card tags
    createOrUpdateMeta('meta[name="twitter:card"]', 'summary_large_image');
    createOrUpdateMeta('meta[name="twitter:site"]', '@globalitacademy');
    createOrUpdateMeta('meta[name="twitter:creator"]', '@globalitacademy');
    createOrUpdateMeta('meta[name="twitter:title"]', courseTitle);
    createOrUpdateMeta('meta[name="twitter:description"]', socialDescription);
    createOrUpdateMeta('meta[name="twitter:image"]', shareImage);
    createOrUpdateMeta('meta[name="twitter:image:alt"]', courseTitle);
    createOrUpdateMeta('meta[name="twitter:url"]', fullUrl);

    // Course-specific Twitter tags
    if (course.group_price) {
      createOrUpdateMeta('meta[name="twitter:label1"]', 'Գին');
      createOrUpdateMeta('meta[name="twitter:data1"]', `${course.group_price.toLocaleString()} դր․/ամիս`);
    }
    
    if (course.duration_months) {
      createOrUpdateMeta('meta[name="twitter:label2"]', 'Տևողություն');
      createOrUpdateMeta('meta[name="twitter:data2"]', `${course.duration_months} ամիս`);
    }

    // Telegram-specific meta tags
    createOrUpdateMeta('meta[name="telegram:card"]', 'summary_large_image');
    createOrUpdateMeta('meta[name="telegram:site"]', '@globalitacademy');
    createOrUpdateMeta('meta[name="telegram:title"]', courseTitle);
    createOrUpdateMeta('meta[name="telegram:description"]', socialDescription);
    createOrUpdateMeta('meta[name="telegram:image"]', shareImage);

    // WhatsApp-specific meta tags (uses Open Graph)
    createOrUpdateMeta('meta[property="whatsapp:title"]', courseTitle, true);
    createOrUpdateMeta('meta[property="whatsapp:description"]', socialDescription, true);
    createOrUpdateMeta('meta[property="whatsapp:image"]', shareImage, true);

    // Additional meta tags for better SEO
    createOrUpdateMeta('meta[name="robots"]', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    createOrUpdateMeta('meta[name="googlebot"]', 'index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1');
    createOrUpdateMeta('meta[name="bingbot"]', 'index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

  }, [course, path]);

  return null;
};